import dayjs from "dayjs";
import duration from 'dayjs/plugin/duration'
dayjs.extend(duration);

/**Remove os espaços entre as palavras */
export function removerEspacos(str: string): string {
    return String(str).replace(/\s+/g, '');
}

/**retorna a ultima posição de um array
* 
* (opcional) pode passar um segundo argumento `posicao` para retornar a posicao do array
* 
* (padrão) pode passar um segundo argumento `valor` para retornar o valor da posicao 
*/
export function ultimaPosicao(array: any[], retornar?: 'posicao' | 'valor') {
    if (retornar === 'posicao') {
        return array?.slice(-1);
    }
    return array?.slice(-1)?.[0];
}


/**Remove os acentos de uma string */
export function removerAcentos(str: string): string {
    return String(str).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}


export function leadZero(numero: string | number) {
    return String(numero)?.padStart(2, '0');
}

/**Retorna apenas o nome do estádio, removendo a descrição que vem após o hífen
 *
 * o formato padrão do estádio é `nome` `-` `descrição`, ex: `R3 - segundo trifólio` retorna `R3`
 *
 * o corte considera apenas o hífen cercado por espaços (` - `), para não truncar
 * nomes que possuem hífen como parte da palavra, ex: `pré-emergência da soja`
 *
 * caso não possua o separador, retorna o texto completo
*/
export function nomeEstadio(estadio: string): string {
    return String(estadio ?? '')?.split(' - ')?.[0]?.trim();
}

/**retorna o próprio index fornecido, apenas para eviar warning do SonarQube */
export function gerarKey(index: number): number {
    return index
}


/**Deixa que apenas números sejam digitados */
export const apenasNumeros = value => {
    const str = String(value).trim();
    const isNegative = str.startsWith('-');
    const numericPart = str.replace(/\D/g, '');
    return isNegative ? `-${numericPart}` : numericPart;
};
// export const apenasNumeros = value => {
//     return String(value)?.replace(/\D/g, '') ?? value;
// }

export const apenasNumerosDecimais = (value: string | number, normalizar?: boolean) => {
    const str = String(value).trim();

    const isNegative = str.startsWith('-');

    let separatorFound = false;
    const numericPart = str
        .replace(/[^0-9.,]/g, '')
        .replace(/[.,]/g, (match) => {
            if (!separatorFound) {
                separatorFound = true;
                return match;
            }
            return '';
        });

    let result = (numericPart.startsWith('.') || numericPart.startsWith(','))
        ? '0' + numericPart
        : numericPart;

    const match = result.match(/^([^.,]+)[.,](.*)$/);
    if (match) {
        const integerPart = match[1];
        const decimalPart = match[2].slice(0, 2);
        const separator = normalizar ? '.' : (result.includes(',') ? ',' : '.');
        result = `${integerPart}${separator}${decimalPart}`;
    }

    if (normalizar) {
        result = result.replace(',', '.');
    }

    return isNegative ? `-${result}` : result;
};



/**Aplica a máscara de horário `HH:MM` enquanto o usuário digita, limitando hora em 23 e minuto em 59 */
export const mascaraHora = (value: string | number): string => {
    const numeros = String(value ?? '').replace(/\D/g, '').slice(0, 4);

    if (numeros === '') return '';

    let hora = numeros.slice(0, 2);
    let minuto = numeros.slice(2, 4);

    if (hora.length === 2 && Number(hora) > 23) hora = '23';
    if (minuto.length === 2 && Number(minuto) > 59) minuto = '59';

    return minuto === '' ? hora : `${hora}:${minuto}`;
};

/**Completa a hora mascarada (`HH:MM`) com os segundos para enviar para a API
 *
 * exemplos: `"18:30"` → `18:30:00`, `"8"` → `08:00:00`, `""` → `''`
*/
export const horaComSegundos = (value: string | number): string => {
    const numeros = String(value ?? '').replace(/\D/g, '');

    if (numeros === '') return '';

    const hora = mascaraHora(numeros);
    const [horas, minutos = '00'] = hora.split(':');

    return `${horas.padStart(2, '0')}:${minutos.padEnd(2, '0')}:00`;
};

/**Valida se o valor está no formato `HH:MM` completo. Vazio é considerado válido pois o campo é opcional */
export const horaValida = (value: string): boolean => {
    if (!value) return true;
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
};

/**Converte um array de `strings` para um array de `números` */
export function parseArrayNumeros(array: string[] | number[]): number[] {
    return (array ?? [])?.map(item => Number(item));
}

/**Converte um array de `números` para um array de `strings` */
export function parseArrayStrings(array: number[] | string[]) {
    return (array ?? [])?.map(item => String(item));
}



export const mascaraData = (value: string) => {
    return String(value)
        .replace(/\D/g, '') // substitui qualquer caractere que não seja número por nada
        .slice(0, 8) // limita a 8 caracteres numéricos (ddMMyyyy)
        .replace(/(\d{2})(\d)/, '$1/$2') // adiciona `/` após 2 números digitados
        .replace(/(\d{2})(\d)/, '$1/$2'); // adiciona `/` após 2 números digitados na sequência
};
/**Arredonda um numero `decimal` para `inteiro` 
 * 
 * tipos: 
 * 
 * `auto (padrão):` Arredonda o número para o decimal mais perto
 * 
 * exemplo: `5.1 = 5`, `5.7 = 6`
 * 
 * 
 * `up:` Arredonda o número pra cima
 * 
 * exemplo: `5.1 = 6`, `5.7 = 6`
 * 
 * `down:` Arredonda o número pra baixo
 * 
 * exemplo: `5.1 = 5`, `5.7 = 5`
 * 
*/
export function arredondarNumero(value: number, type?: 'auto' | 'up' | 'down') {
    if (type === 'up') {
        return Math.ceil(value);
    }
    if (type === 'down') {
        return Math.floor(value);
    }

    return Math.round(value);
}


export const formatarValor = (valor: number): string => {
    const valorArredondado = Number(valor).toFixed(2);
    const partes = valorArredondado.split('.');
    const inteiro = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimal = partes[1] ? ',' + partes[1] : '';
    return inteiro + decimal + ' ha';
}


/**remove `amp;` e subistitui por `""` (aspas) */
export function substituirAsc(str: string): string {

    let remove = String(str)?.replace(/quot;|&amp;/g, '"');
    let fraseCorrigida = remove.replace(/amp;+/g, "");


    return fraseCorrigida ?? str;
}

export function maskCNPJ(cnpj) {
    if (!cnpj) return ""
    cnpj = cnpj.replace(/\D/g, "");

    cnpj = cnpj.replace(/^(\d{2})(\d)/, "$1.$2");
    cnpj = cnpj.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
    cnpj = cnpj.replace(/\.(\d{3})(\d)/, ".$1/$2");
    cnpj = cnpj.replace(/(\d{4})(\d)/, "$1-$2");

    return cnpj;
}


export const convertSegundosParaHora = (segundos: number) => {
    return dayjs.duration(segundos, 'seconds').format('HH:mm:ss')
}


export const mascaraHectares = value => {
    const apenasNumeros = String(value).replace(/\D/g, '');

    if (apenasNumeros.length === 0) return '';
    if (apenasNumeros.length === 1) return `0.${apenasNumeros}`;
    if (apenasNumeros.length === 2) return `.${apenasNumeros}`;
    if (apenasNumeros.length === 5) return apenasNumeros?.[0] === '0' ? `${(apenasNumeros?.slice?.(1))?.replace(/^(\d+)(\d{2})$/, '$1.$2')}` : `${apenasNumeros.replace(/^(\d+)(\d{2})$/, '$1.$2')}`;

    return apenasNumeros.replace(/^(\d+)(\d{2})$/, '$1.$2');
};
// export const mascaraHectares = value => {
//     const str = String(value).replace(/\D/g, '');

//     return str.replace(/(\d{2})$/, '.$1');
// };

export const NumberArea = value => {
    let conv = Number(mascaraHectares(value))
    return conv;
};

/**
 * Converte um valor de área para número, aceitando os formatos usados no sistema:
 * "99.17", "99,17", "99.17 ha", 99.17 e "1.234,56".
 * Diferente de `mascaraHectares` (que é máscara de digitação), aqui o valor não é
 * reformatado — apenas convertido. Retorna 0 quando não há número válido.
 */
export const areaParaNumero = (value: string | number | null | undefined): number => {
    if (value === null || value === undefined || value === '') return 0;

    // remove unidade e demais caracteres, mantendo apenas dígitos e separadores
    let limpo = String(value).replace(/[^0-9,.-]/g, '').trim();

    // formato brasileiro (1.234,56): remove o separador de milhar
    if (limpo.includes(',')) {
        limpo = limpo.replace(/\./g, '').replace(',', '.');
    }

    const numero = Number(limpo);
    return isNaN(numero) ? 0 : numero;
};

/**
 * Formata um valor numérico para o padrão monetário pt-BR (separador de milhares e duas casas decimais).
 * Entrada aceita: number | string (ex.: "1234.5" ou "1.234,50" será normalizado).
 * Retorno: string formatada, ou string vazia para entradas inválidas.
 */
export function convertMilhar(value: number | string | null | undefined): string {
    if (value === null || value === undefined) return '';

    // Normaliza string para número: aceita "1.234,56" ou "1234.56" ou "1234"
    let numeric: number;
    if (typeof value === 'string') {
        // Remove espaços
        const trimmed = value.trim();
        if (trimmed === '') return '';

        // Se string contém vírgula assume-se formato pt-BR; substitui separador de milhar e transforma vírgula em ponto
        if (trimmed.includes(',')) {
            // Remove pontos de milhar e troca vírgula decimal por ponto
            const normalized = trimmed.replace(/\./g, '').replace(',', '.');
            numeric = Number(normalized);
        } else {
            // Tenta parse direto (ponto decimal)
            numeric = Number(trimmed);
        }
    } else {
        numeric = value;
    }

    if (!isFinite(numeric) || Number.isNaN(numeric)) return '';

    // Usa Intl.NumberFormat para garantir comportamento consistente e regras de arredondamento corretas
    const formatter = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    // Garantir arredondamento para 2 casas decimais
    // (Intl já faz, mas forçamos a precisão evitando imprecisões binárias)
    const rounded = Math.round((numeric + Number.EPSILON) * 100) / 100;

    return formatter.format(rounded);
}

export const transition_default = { duration: 1, ease: [0, 0.71, 0.2, 1.01], exit: { duration: 0.1, ease: [0, 1.21, 0.77, 0.91] } }


export function numeroMilhar(valor: number | string) {
    const valor_formatado = Number(valor);

    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: valor_formatado % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
    }).format(valor_formatado);
}



export const capitalizeWords = (str: string) => {
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

/**Aplica a máscara de odômetro/horímetro `000.000` (ponto como separador de milhar), limitado a 7 dígitos */
export const mascaraOdometro = (value: string | number): string => {
    const digitos = String(value ?? '').replace(/\D/g, '').slice(0, 7);

    return digitos.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**Aplica a máscara decimal enquanto o usuário digita, preenchendo as casas da direita para a esquerda
 *
 * `casas` define a quantidade de decimais (padrão `2`), use `0` para aceitar apenas inteiros
 *
 * exemplos (com 2 casas): `4` → `0,04`, `455` → `4,55`, `123456` → `1.234,56`
 *
 * pode ser aplicada em cima do próprio valor já mascarado, então serve tanto no `value` quanto no `onChange`
 *
 * para enviar o valor para a API use `desmascararNumero`
*/
export const mascaraAoDigitar = (value: string | number, casas = 2): string => {
    // remove os zeros à esquerda para o valor não travar em 0,00 conforme o usuário digita
    const digitos = String(value ?? '').replace(/\D/g, '').replace(/^0+(?=\d)/, '');

    if (digitos === '') return '';

    if (casas <= 0) {
        return digitos.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    const preenchido = digitos.padStart(casas + 1, '0');
    const corte = preenchido.length - casas;
    const inteiro = preenchido.slice(0, corte).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${inteiro},${preenchido.slice(corte)}`;
};

/**Aplica a máscara de valor monetário `1.234,56`
 *
 * a digitação é lida em centavos: `12345` vira `123,45`
*/
export const mascaraValor = (value: string | number): string => mascaraAoDigitar(value, 2);

/**Prepara um valor decimal vindo da API para ser exibido em um campo que usa `mascaraAoDigitar`/`mascaraValor`
 *
 * aceita o decimal com ponto (`"45.5"`, `1234.56`) ou já no padrão pt-BR (`"1.234,56"`)
 *
 * exemplos: `"45.5"` → `45,50`, `"1234.56"` → `1.234,56`, `null` → `''`
*/
export const mascararValorInicial = (value: string | number, casas = 2): string => {
    const texto = String(value ?? '').trim();

    if (texto === '') return '';

    // a vírgula indica o padrão pt-BR (1.234,56), onde o ponto é separador de milhar
    const limpo = texto.includes(',') ? desmascararNumero(texto) : texto.replace(/[^\d.-]/g, '');

    if (!/\d/.test(limpo)) return '';

    const numero = Number(limpo);

    if (!isFinite(numero)) return '';

    return mascaraAoDigitar(numero.toFixed(casas), casas);
};

/**Remove a parte decimal de um valor, mantendo apenas o inteiro
 *
 * exemplos: `"125000.00"` → `125000`, `"1.234,56"` → `1234`
*/
export const removerDecimal = (value: string | number): string => {
    const texto = String(value ?? '').trim();

    if (texto === '') return '';

    // com vírgula presente ela é o separador decimal e o ponto é separador de milhar
    const inteiro = texto.includes(',')
        ? texto.split(',')[0].replace(/\./g, '')
        : texto.split('.')[0];

    return inteiro.replace(/\D/g, '');
};

/**Remove a máscara de `mascaraValor`/`mascaraOdometro` e devolve o número pronto para a API
 *
 * o ponto sempre é separador de milhar nessas máscaras, então é removido, e a vírgula vira ponto decimal
 *
 * exemplos: `1.234,56` → `1234.56`, `125.000` → `125000`
*/
export const desmascararNumero = (value: string | number): string => {
    const texto = String(value ?? '').trim();

    if (texto === '') return '';

    return texto.replace(/\./g, '').replace(',', '.');
};